import React from "react";
import { Dialog, DialogTitle, DialogContent, Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IconMap, BOOTH_ICON_OPTIONS, QPR_ONLY_ICON_OPTIONS } from "../../utils/IconMap";
import LocalText from "../common/LocalText";
import { useSpaceId } from "../../hooks/useSpaceId";

// Space id QPR was assigned in this database — tied to this specific environment, not a
// generic feature flag. If this app is ever deployed against a different schema/seed, this
// id may need to change.
const QPR_SPACE_ID = 11;

// Visual icon-tile grid picker for booths — replaces the old implicit "hope the booth name
// matches a known icon" behavior with an explicit, visual choice. Clicking a tile selects it
// and closes the picker in one step, matching SelectTypeModal's immediate-select pattern.
const IconPickerModal = ({ open, onClose, value, onSelect, prefix = "bth" }) => {
  const spaceId = useSpaceId();
  const options = spaceId === QPR_SPACE_ID
    ? [...BOOTH_ICON_OPTIONS, ...QPR_ONLY_ICON_OPTIONS]
    : BOOTH_ICON_OPTIONS;

  return (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="sm"
    PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
  >
    <DialogTitle sx={{ bgcolor: "primary.main", color: "white", pr: 6, py: 2.5 }}>
      <Typography variant="h6" fontWeight={700} component="span">
        <LocalText>Choose an Icon</LocalText>
      </Typography>
      <IconButton
        onClick={(e) => { e.currentTarget.blur(); onClose(); }}
        size="small"
        sx={{ position: "absolute", right: 10, top: 12, color: "white" }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>

    <DialogContent dividers sx={{ py: 3 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))",
          gap: 2,
        }}
      >
        {options.map((opt) => {
          const isSelected = value === opt.key;
          const src = IconMap[`${prefix}_${opt.key}`];
          return (
            <Box
              key={opt.key}
              onClick={() => { onSelect(opt.key); onClose(); }}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.75,
                cursor: "pointer",
                p: 1,
                borderRadius: 2,
                border: "2px solid",
                borderColor: isSelected ? "primary.main" : "transparent",
                bgcolor: isSelected ? "rgba(10,50,109,0.06)" : "transparent",
                transition: "border-color 0.15s ease, background-color 0.15s ease",
                userSelect: "none",
                "&:hover": { bgcolor: "rgba(10,50,109,0.04)" },
              }}
            >
              <Box
                sx={{
                  bgcolor: "#0a326d",
                  borderRadius: "12px",
                  width: 52,
                  height: 52,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(10,50,109,0.25)",
                  flexShrink: 0,
                }}
              >
                <Box component="img" src={src} alt={opt.label} sx={{ width: 30, height: 30 }} />
              </Box>
              <Typography
                variant="caption"
                sx={{ textAlign: "center", fontSize: 11, fontWeight: 600, color: "#1e3a5f", lineHeight: 1.2 }}
              >
                <LocalText>{opt.label}</LocalText>
              </Typography>
            </Box>
          );
        })}
      </Box>
    </DialogContent>
  </Dialog>
  );
};

export default IconPickerModal;
